import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import {
  isNativeIOS,
  checkNativePushPermission,
  registerNativePush,
  nativePushRecentlyRegistered,
  onNativePushTap,
} from '../utils/capacitor';
import { refreshWebPushRegistration } from '../utils/webPush';
import { isNativeAndroid, nativePushAvailable } from '../utils/capacitor';

// iOS native push plumbing, mounted once inside the authenticated app shell.
//
//   1. Keep the APNs token fresh: if the user already granted permission,
//      silently re-register at most every 12 hours (tokens can rotate after
//      restores/OS updates, and the server clears tokens APNs reports dead).
//      We never PROMPT here — the value-first NotificationPrompt / Settings
//      button own the ask.
//   2. Route taps: the backend puts a `url` in every push payload
//      ('/scan', '/coach', '/analysis', ...). When the user taps a
//      notification we navigate there with React Router.
//
// On the web and the Android TWA it does the equivalent of (1) for web push:
// the server keeps one subscription per device, so each device must re-register
// itself or it silently stops receiving (that is how a Pixel went quiet after
// the iPhone's browser registered — the old single slot was overwritten and
// nothing ever put the Pixel back).
export function usePushRegistration(): void {
  const navigate = useNavigate();

  useEffect(() => {
    // Native Android app built without Firebase: no push channel (its web view has no web push).
    if (isNativeAndroid() && !nativePushAvailable()) return;
    if (!isNativeIOS() && !isNativeAndroid()) {
      void refreshWebPushRegistration(API_BASE_URL);
      return;
    }

    let cleanupTap: (() => void) | null = null;

    (async () => {
      try {
        const status = await checkNativePushPermission();
        if (status === 'granted' && !nativePushRecentlyRegistered()) {
          await registerNativePush(API_BASE_URL);
        }
      } catch {
        // silent — registration is retried next launch
      }

      cleanupTap = await onNativePushTap((url) => {
        if (url && url.startsWith('/')) navigate(url);
        else navigate('/dashboard');
      });
    })();

    return () => { if (cleanupTap) cleanupTap(); };
  }, [navigate]);
}
